import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import TopNav from './TopNav';
import MobileNav from './MobileNav';
import NotificationContainer from '../Notification/NotificationContainer';
import ConfirmModal from '../Notification/ConfirmModal';
import PageLoader from '../UI/PageLoader';
import { usePageLoaderContext } from '../../contexts/PageLoaderContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useNotification } from '../../contexts/NotificationContext';
import styles from '../../styles/Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { isLoading, message } = usePageLoaderContext();
  const confirmService = useConfirm();
  const notificationService = useNotification();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    if (!isMobile) {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className={styles.app}>
      {/* Sidebar */}
      <Sidebar 
        collapsed={sidebarCollapsed}
        isMobile={isMobile}
        onToggle={toggleSidebar}
        user={user}
      />

      {/* Mobile Navigation Overlay */}
      <MobileNav 
        isOpen={mobileMenuOpen}
        onClose={closeMobileMenu}
        user={user}
      />

      {/* Main Content */}
      <main className={`${styles.mainContent} ${sidebarCollapsed ? styles.collapsed : ''}`}>
        <div className={styles.content}>
          {/* Page Content */}
          {children}
        </div>
      </main>

      {/* Page Loader */}
      <PageLoader isVisible={isLoading} message={message} />

      {/* Top Navigation */}
      <TopNav 
        collapsed={sidebarCollapsed}
        isMobile={isMobile}
        onMobileMenuToggle={toggleMobileMenu}
        user={user}
      />

      {/* Notification Container */}
      <NotificationContainer 
        notifications={notificationService.notifications}
        onRemove={notificationService.remove}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmService.isOpen}
        title={confirmService.title}
        message={confirmService.message}
        okText={confirmService.options.okText}
        cancelText={confirmService.options.cancelText}
        onConfirm={confirmService.onConfirm}
        onCancel={confirmService.onCancel}
      />
    </div>
  );
};

export default Layout;